import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { supabase } from '../../services/supabase';
import { useNavigate } from 'react-router-dom';
import type { Game, Player, WinnerType, RoleType } from '../../types/models';
import { fixTypes } from '../../services/adminPermissions';

interface ScrapedGame {
  gameId: string;
  date: string;
  judge: string;
  winner: string;
  players: ScrapedPlayer[];
  videoLink?: string;
  game_num?: number; // Game number extracted from title
}

interface GameListItem {
  gameId: string;
  title: string;
  number: number | null;
  date: string;
  url: string;
}

interface ScrapedPlayer {
  position: number;
  nickname: string;
  role: string;
  isWin: boolean;
  bonusPoints: number;
  fouls: number;
  isKilledFirst: boolean;
  winningMove?: string;
}

interface PlayerMapping {
  externalNickname: string;
  localPlayerId: string | null;
  position: number;
  requiresMapping: boolean;
}

interface PlayerMappingDialogProps {
  open: boolean;
  onClose: () => void;
  playerMapping: PlayerMapping;
  players: Player[];
  onSave: (playerId: string) => void;
}

// Player mapping dialog component
const PlayerMappingDialog: React.FC<PlayerMappingDialogProps> = ({
  open,
  onClose,
  playerMapping,
  players,
  onSave
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  
  // Filter players to find most likely matches based on substring matching
  const suggestedPlayers = players.filter(player => 
    player.nickname.toLowerCase().includes(playerMapping.externalNickname.toLowerCase()) ||
    playerMapping.externalNickname.toLowerCase().includes(player.nickname.toLowerCase())
  ).slice(0, 5); // Limit to top 5 suggestions
  
  const otherPlayers = players.filter(player => 
    !suggestedPlayers.some(p => p.id === player.id)
  );

  const handlePlayerChange = (event: SelectChangeEvent) => {
    setSelectedPlayerId(event.target.value);
  };

  const handleSave = () => {
    if (selectedPlayerId) {
      onSave(selectedPlayerId);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Map Player: {playerMapping.externalNickname}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Typography>
            Player "{playerMapping.externalNickname}" from the external source needs to be mapped to a player in your system.
          </Typography>
          
          {suggestedPlayers.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Suggested Players
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {suggestedPlayers.map(player => (
                  <Button 
                    key={player.id}
                    variant={selectedPlayerId === player.id ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setSelectedPlayerId(player.id)}
                    sx={{ mb: 1 }}
                  >
                    {player.nickname} ({player.full_name})
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
          
          <FormControl fullWidth>
            <InputLabel>Select Player</InputLabel>
            <Select
              value={selectedPlayerId}
              onChange={handlePlayerChange}
              label="Select Player"
            >
              {suggestedPlayers.length > 0 && (
                <MenuItem disabled sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                  --- Suggested Players ---
                </MenuItem>
              )}
              {suggestedPlayers.map(player => (
                <MenuItem key={player.id} value={player.id}>
                  {player.nickname} ({player.full_name})
                </MenuItem>
              ))}
              
              {otherPlayers.length > 0 && (
                <MenuItem disabled sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                  --- All Players ---
                </MenuItem>
              )}
              {otherPlayers.map(player => (
                <MenuItem key={player.id} value={player.id}>
                  {player.nickname} ({player.full_name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={!selectedPlayerId}
        >
          Save Mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GameScraper: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isGameList, setIsGameList] = useState<boolean>(false);
  const [gameListItems, setGameListItems] = useState<GameListItem[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [processingBatchImport, setProcessingBatchImport] = useState<boolean>(false);
  const [currentBatchIndex, setCurrentBatchIndex] = useState<number>(0);
  const [scrapedGame, setScrapedGame] = useState<ScrapedGame | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leagues, setLeagues] = useState<any[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [judges, setJudges] = useState<Player[]>([]);
  const [leagueId, setLeagueId] = useState<string>('');
  const [sessionNum, setSessionNum] = useState<number>(1);
  const [gameNumber, setGameNumber] = useState<number>(1);
  const [mappingDialogOpen, setMappingDialogOpen] = useState<boolean>(false);
  const [currentMapping, setCurrentMapping] = useState<PlayerMapping | null>(null);
  const [playerMappings, setPlayerMappings] = useState<Map<string, string>>(new Map());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Fetch initial data
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchLeagues(),
        fetchPlayers(),
        fetchJudges(),
        fetchNicknameMappings()
      ]);
      console.log('GameScraper component initialized with all data');
    };
    
    loadData();
  }, []);
  
  // Implement complete batch processing logic
  const [batchQueue, setBatchQueue] = useState<string[]>([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState<boolean>(false);
  const [isProcessingCurrent, setIsProcessingCurrent] = useState<boolean>(false);
  const [totalGamesInBatch, setTotalGamesInBatch] = useState<number>(0);
  
  // This effect starts the batch process when requested
  useEffect(() => {
    if (processingBatchImport && !isBatchProcessing && selectedGames.size > 0) {
      console.log(`Starting batch import of ${selectedGames.size} games...`);
      // Convert selected games to an array and set as the processing queue
      const gameQueue = Array.from(selectedGames);
      setTotalGamesInBatch(gameQueue.length);
      setBatchQueue(gameQueue);
      setCurrentBatchIndex(0);
      setIsBatchProcessing(true);
    }
  }, [processingBatchImport, selectedGames, isBatchProcessing]);
  
  // Use a ref to track the games we've imported in this batch
  const processedGamesRef = React.useRef<Set<string>>(new Set());
  
  // Reset processed games when batch starts
  useEffect(() => {
    if (processingBatchImport && isBatchProcessing) {
      processedGamesRef.current = new Set();
    }
  }, [processingBatchImport, isBatchProcessing]);
  
  // This effect handles the actual processing of games one by one
  useEffect(() => {
    const processNextGameInQueue = async () => {
      // Only run if we're in batch processing mode, have games in the queue, and aren't already processing a game
      if (!isBatchProcessing || batchQueue.length === 0 || isProcessingCurrent) return;
      
      // Indicate that we're processing a game
      setIsProcessingCurrent(true);
      
      // Take the first game from the queue
      const currentGameId = batchQueue[0];
      
      // Skip if we've already processed this game in this batch
      if (processedGamesRef.current.has(currentGameId)) {
        console.log(`Skipping game ${currentGameId} - already processed in this batch`);
        setBatchQueue(prev => prev.slice(1)); // Remove from queue
        setIsProcessingCurrent(false); // Release processing lock
        return;
      }
      
      console.log(`Processing game ${currentBatchIndex + 1}/${totalGamesInBatch}: ${currentGameId}`);
      
      try {
        // Scrape the current game
        await scrapeGame(currentGameId);
        
        // Wait a moment for state to update
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Create the game if we have scraped data
        if (scrapedGame) {
          console.log(`Creating game for: ${currentGameId}`);
          await createGame(true); // Pass true to indicate batch processing
          
          // Allow time for the game to be created
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          console.log(`Successfully processed game: ${currentGameId}`);
          
          // Mark this game as processed using the ref (immune to React's rendering cycle)
          processedGamesRef.current.add(currentGameId);
        } else {
          console.warn(`No scraped data available for game: ${currentGameId}`);
        }
        
        // Always update the progress counter
        setCurrentBatchIndex(prev => prev + 1);
        
        // Get a local copy of the current batch queue for checking completion
        const currentBatchQueue = [...batchQueue];
        const isLastGame = currentBatchQueue.length === 1;
        
        // Remove the processed game from the queue
        setBatchQueue(prev => prev.slice(1));
        
        // If this was the last game, complete the batch
        if (isLastGame) {
          console.log(`Finishing batch import - processed ${processedGamesRef.current.size}/${totalGamesInBatch} games`);
          setTimeout(() => {
            setProcessingBatchImport(false);
            setIsBatchProcessing(false);
            showSnackbar(`Finished importing ${processedGamesRef.current.size} games`, 'success');
            setSelectedGames(new Set());
          }, 1000);
        }
      } catch (error) {
        console.error(`Error processing game ${currentGameId}:`, error);
      } finally {
        // Mark this game as done processing
        setTimeout(() => {
          setIsProcessingCurrent(false);
        }, 500);
      }
    };
    
    processNextGameInQueue();
  }, [batchQueue, isBatchProcessing, isProcessingCurrent, totalGamesInBatch, currentBatchIndex]);
  
  // This effect handles the case where the batch queue becomes empty but the batch wasn't marked as complete
  useEffect(() => {
    if (isBatchProcessing && batchQueue.length === 0 && !isProcessingCurrent && currentBatchIndex > 0) {
      // This is a fallback for cases where the batch completion wasn't triggered in the main loop
      console.log("Fallback batch completion triggered");
      setProcessingBatchImport(false);
      setIsBatchProcessing(false);
      showSnackbar(`Finished importing ${totalGamesInBatch} games`, 'success');
      setSelectedGames(new Set());
    }
  }, [batchQueue, isBatchProcessing, isProcessingCurrent, currentBatchIndex, totalGamesInBatch]);

  const fetchLeagues = async (judgeId?: string) => {
    try {
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Start with base query
      let baseQuery = supabase
        .from('leagues')
        .select(`
          id,
          name,
          judge_id,
          created_at,
          club:club_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `);
      
      // If a judge ID is provided, filter leagues to only show those with this judge
      if (judgeId) {
        baseQuery = baseQuery.eq('judge_id', judgeId);
      }
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let queryResult;
      
      if (superAdmin) {
        // Superadmins see all leagues (with optional judge filter)
        queryResult = await baseQuery.order('created_at', { ascending: false });
      } else {
        // Regular admins only see leagues from their club
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          queryResult = { data: [], error: null };
        } else {
          // Filter leagues by the admin's club
          queryResult = await baseQuery
            .eq('club_id', userRole.club_id)
            .order('created_at', { ascending: false });
        }
      }
      
      const { data, error } = queryResult;
      
      if (error) throw error;
      
      console.log(`Fetched ${data?.length || 0} leagues${judgeId ? ' for judge ' + judgeId : ''}`);
      setLeagues(data || []);
      
      // Set the default league to the most recently created one (first in the list)
      if (data && data.length > 0) {
        // Cast to any to bypass TypeScript errors until database schema is updated
        const leagues = data as any[];
        if (leagues.length > 0) {
          setLeagueId(leagues[0].id);
          console.log(`Auto-selected league: ${leagues[0].name}`);
        }
      } else if (!judgeId) {
        // Only reset if we're not filtering by a specific judge
        setLeagueId('');
      }
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
      showSnackbar('Failed to load leagues', 'error');
    }
  };

  const fetchPlayers = async () => {
    try {
      // Players don't have club_id, so no need for admin filtering
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .order('nickname', { ascending: true });

      if (error) throw error;
      
      // Fix types to ensure proper typing
      const typedData = fixTypes<Player>(data || []);
      setPlayers(typedData);
    } catch (error: any) {
      console.error('Error fetching players:', error.message);
      showSnackbar('Failed to load players', 'error');
    }
  };

  const fetchJudges = async () => {
    try {
      // Players don't have club_id, so no need for admin filtering
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('is_judge', true)
        .order('nickname', { ascending: true });

      if (error) throw error;
      
      // Fix types to ensure proper typing
      const typedData = fixTypes<Player>(data || []);
      setJudges(typedData);
    } catch (error: any) {
      console.error('Error fetching judges:', error.message);
      showSnackbar('Failed to load judges', 'error');
    }
  };
  
  const fetchNicknameMappings = async () => {
    try {
      // Nickname mappings don't need club filtering since players don't have club_id
      const { data, error } = await supabase
        .from('nickname_mappings')
        .select(`
          id,
          external_nickname,
          player_id,
          source
        `);

      if (error) throw error;
      
      // Convert the array of mappings to a Map for efficient lookups
      const mappings = new Map<string, string>();
      if (data) {
        data.forEach(mapping => {
          mappings.set(mapping.external_nickname, mapping.player_id);
        });
      }
      
      setPlayerMappings(mappings);
      console.log(`Loaded ${mappings.size} existing nickname mappings`);
    } catch (error: any) {
      console.error('Error fetching nickname mappings:', error.message);
      showSnackbar('Failed to load nickname mappings', 'error');
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputUrl = e.target.value;
    setUrl(inputUrl);
    
    // Detect if this might be a game list URL
    const isGameListUrl = inputUrl.includes('/rate-game-list/');
    setIsGameList(isGameListUrl);
    
    // Reset game list and selected game when URL changes
    if (isGameListUrl !== isGameList) {
      setGameListItems([]);
      setSelectedGameId('');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const scrapeGameList = async () => {
    if (!url) {
      setError('Please enter a URL.');
      return;
    }

    setLoading(true);
    setError(null);
    setScrapedGame(null);
    setGameListItems([]);

    try {
      // Extract list ID from URL
      const listIdMatch = url.match(/\/rate-game-list\/(\d+)/);
      if (!listIdMatch || !listIdMatch[1]) {
        throw new Error('Invalid game list URL format. Expected format: https://the-mafia.net/rate-game-list/LIST_ID');
      }

      const listId = listIdMatch[1];
      console.log('Scraping game list ID:', listId);

      // Call the Edge Function to scrape the game list
      const { data, error } = await supabase.functions.invoke('scrape-game-list', {
        body: { listId }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data || !data.games || !Array.isArray(data.games)) {
        throw new Error('No game list data returned from scraper');
      }

      console.log('Scraped game list:', data);
      
      // Store the game list
      setGameListItems(data.games);
      
      // If games were found, automatically select all of them by default
      if (data.games.length > 0) {
        // Select all games
        const allGameIds = data.games.map((game: GameListItem) => game.gameId);
        setSelectedGames(new Set(allGameIds));
        showSnackbar(`Found and selected ${data.games.length} games in list`, 'success');
      } else {
        showSnackbar('No games found in the list', 'warning');
      }
    } catch (err: any) {
      console.error('Error scraping game list:', err);
      setError(err.message || 'Failed to scrape game list.');
      showSnackbar('Failed to scrape game list', 'error');
    } finally {
      setLoading(false);
    }
  };

  const scrapeGame = async (gameId?: string) => {
    // If we're in game list mode and no gameId is provided, use the selected game
    // If we're in single game mode, extract the gameId from the URL
    let targetGameId = gameId;
    
    if (!targetGameId) {
      if (isGameList) {
        if (!selectedGameId) {
          setError('Please select a game from the list.');
          return;
        }
        targetGameId = selectedGameId;
      } else {
        if (!url) {
          setError('Please enter a game URL.');
          return;
        }

        // Extract game ID from URL
        const gameIdMatch = url.match(/\/rate_game\/(\d+)/);
        if (!gameIdMatch || !gameIdMatch[1]) {
          setError('Invalid URL format. Expected format: https://the-mafia.net/rate_game/GAME_ID');
          return;
        }
        targetGameId = gameIdMatch[1];
      }
    }

    setLoading(true);
    setError(null);
    setScrapedGame(null);

    try {
      console.log('Scraping game ID:', targetGameId);

      // Call the Edge Function to scrape the game
      const { data, error } = await supabase.functions.invoke('scrape-game-results', {
        body: { gameId: targetGameId }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('No data returned from scraper');
      }

      console.log('Scraped data:', data);
      setScrapedGame(data);

      // Find judge ID based on the scraped judge nickname
      const judgeId = findJudgeByNickname(data.judge);
      if (judgeId) {
        console.log(`Found judge ID for ${data.judge}: ${judgeId}`);
        
        // Fetch leagues for this judge specifically
        await fetchLeagues(judgeId);
      } else {
        console.log(`Judge not found for nickname: ${data.judge}`);
        // If judge not found, still display the scraped data but don't filter leagues
      }

      // Auto-fill session number and game number based on game_num if available
      if (data.game_num) {
        // Calculate session and game number assuming 5 games per session
        // Session numbers start at 1 (first 5 games are session 1, next 5 are session 2, etc.)
        // Game numbers within session also start at 1 (first game in session is game 1)
        const gamesPerSession = 5;
        const rawGameNum = data.game_num;
        
        // Calculate session number (1-based)
        // For games 1-5: session 1, games 6-10: session 2, etc.
        const sessionNum = Math.ceil(rawGameNum / gamesPerSession);
        
        // Calculate game number within session (1-based)
        // For session 1: games 1-5 become games 1-5
        // For session 2: games 6-10 become games 1-5, etc.
        const gameNumber = ((rawGameNum - 1) % gamesPerSession) + 1;
        
        console.log(`Game ${rawGameNum} maps to Session ${sessionNum}, Game ${gameNumber}`);
        
        setSessionNum(sessionNum);
        setGameNumber(gameNumber);
      } else {
        // Default to current year for session_num and 1 for game_number if game_num not available
        console.log(`No game number found, defaulting to current year and game 1`);
        setSessionNum(new Date().getFullYear());
        setGameNumber(1);
      }
      
      showSnackbar('Game data scraped successfully!', 'success');
    } catch (err: any) {
      console.error('Error scraping game:', err);
      setError(err.message || 'Failed to scrape game data.');
      showSnackbar('Failed to scrape game data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleScrape = () => {
    if (isGameList) {
      scrapeGameList();
    } else {
      scrapeGame();
    }
  };
  
  const handleToggleSelectGame = (gameId: string) => {
    const newSelectedGames = new Set(selectedGames);
    if (newSelectedGames.has(gameId)) {
      newSelectedGames.delete(gameId);
    } else {
      newSelectedGames.add(gameId);
    }
    setSelectedGames(newSelectedGames);
  };
  
  const handleSelectAllGames = () => {
    // Select all games
    const allGameIds = gameListItems.map(game => game.gameId);
    setSelectedGames(new Set(allGameIds));
  };
  
  const handleDeselectAllGames = () => {
    // Clear all selections
    setSelectedGames(new Set());
  };
  
  const handleImportSelectedGames = () => {
    if (selectedGames.size === 0) {
      showSnackbar('No games selected for import', 'error');
      return;
    }
    
    // Reset batch processing state
    setCurrentBatchIndex(0);
    setBatchQueue([]);
    setIsBatchProcessing(false);
    
    // Start batch import process - this will trigger our batch processing effect
    setProcessingBatchImport(true);
    showSnackbar(`Starting batch import of ${selectedGames.size} games...`, 'info');
  };

  const convertScrapedWinnerToEnum = (winner: string): WinnerType => {
    if (winner === 'MAFIA') return 'MAFIA';
    if (winner === 'CIVILIANS') return 'CIVILIANS';
    return 'TIE';
  };

  const convertScrapedRoleToEnum = (role: string): RoleType => {
    if (role === 'MAFIA') return 'MAFIA';
    if (role === 'DON') return 'DON';
    if (role === 'SHERIFF') return 'SHERIFF';
    return 'CITIZEN';
  };

  const findJudgeByNickname = (nickname: string): string | null => {
    const judge = judges.find(j => j.nickname.toLowerCase() === nickname.toLowerCase());
    return judge ? judge.id : null;
  };

  const findPlayerByNickname = (nickname: string): string | null => {
    // Normalize the nickname for comparison
    const normalizedNickname = nickname.toLowerCase().trim();
    
    // First check in the nickname mappings (case insensitive)
    // This should be checked first since it's the source of truth for external nicknames
    // Convert to array first to avoid TypeScript iteration issues with ES5 target
    const mappingsArray = Array.from(playerMappings);
    for (let i = 0; i < mappingsArray.length; i++) {
      const [mappedName, playerId] = mappingsArray[i];
      if (mappedName.toLowerCase().trim() === normalizedNickname) {
        console.log(`Found player mapping for "${nickname}" → player ID: ${playerId}`);
        return playerId;
      }
    }
    
    // Then try exact match with local players (case insensitive)
    let player = players.find(p => p.nickname.toLowerCase().trim() === normalizedNickname);
    if (player) {
      console.log(`Found local player for "${nickname}" → ${player.nickname} (ID: ${player.id})`);
      return player.id;
    }

    // Finally try fuzzy match - check if the nickname is contained within local player nicknames
    // This can help with partial matches like "John" matching "JohnDoe"
    player = players.find(p => 
      p.nickname.toLowerCase().includes(normalizedNickname) || 
      normalizedNickname.includes(p.nickname.toLowerCase())
    );
    if (player) {
      console.log(`Found fuzzy match for "${nickname}" → ${player.nickname} (ID: ${player.id})`);
      return player.id;
    }

    console.log(`No match found for player nickname: "${nickname}"`);
    return null;
  };

  const createGame = async (isBatchProcessing: boolean = false) => {
    if (!scrapedGame) return;
    if (!leagueId) {
      showSnackbar('Please select a league', 'error');
      return;
    }

    try {
      // Find judge ID or show error - important for player mapping validation
      const judgeId = findJudgeByNickname(scrapedGame.judge);
      if (!judgeId) {
        showSnackbar(`Judge "${scrapedGame.judge}" not found. Please create the judge first.`, 'error');
        return;
      }
      
      // Get the selected league to verify it has a valid judge
      const selectedLeague = leagues.find(league => league.id === leagueId);
      if (!selectedLeague) {
        showSnackbar('Selected league not found. Please try again.', 'error');
        return;
      }

      // Check if all players can be mapped
      const playerMappingsNeeded: PlayerMapping[] = [];
      console.log(`Checking player mappings for ${scrapedGame.players.length} players...`);
      for (const player of scrapedGame.players) {
        const playerId = findPlayerByNickname(player.nickname);
        if (!playerId) {
          console.log(`Player "${player.nickname}" needs mapping`);
          playerMappingsNeeded.push({
            externalNickname: player.nickname,
            localPlayerId: null,
            position: player.position,
            requiresMapping: true
          });
        }
      }

      // If there are players that need mapping, different behavior based on batch processing
      if (playerMappingsNeeded.length > 0) {
        if (isBatchProcessing) {
          // During batch processing, skip games with unmapped players
          console.log(`Skipping game with gameId ${scrapedGame.gameId} - has unmapped players: ${playerMappingsNeeded.map(p => p.externalNickname).join(', ')}`);
          return;
        } else {
          // In regular mode, show dialog for first unmapped player
          setCurrentMapping(playerMappingsNeeded[0]);
          setMappingDialogOpen(true);
          return;
        }
      }

      // If all players are mapped, proceed with game creation
      await createGameAndResults(isBatchProcessing);
    } catch (error: any) {
      console.error('Error in game creation process:', error);
      showSnackbar('Error: ' + error.message, 'error');
    }
  };

  const handleSavePlayerMapping = async (playerId: string) => {
    if (!currentMapping) return;

    try {
      // Save mapping to database
      const { error } = await supabase.from('nickname_mappings').insert({
        external_nickname: currentMapping.externalNickname,
        player_id: playerId,
        source: 'mafia.net'
      });

      if (error) throw error;

      // Update local mappings
      setPlayerMappings(new Map(playerMappings.set(currentMapping.externalNickname, playerId)));
      
      // Close dialog
      setMappingDialogOpen(false);
      
      // Check if there are more players to map
      if (scrapedGame) {
        const remainingMappings: PlayerMapping[] = [];
        for (const player of scrapedGame.players) {
          // Use the enhanced findPlayerByNickname function which already checks playerMappings
          const playerId = findPlayerByNickname(player.nickname);
          
          if (!playerId) {
            console.log(`Player "${player.nickname}" still needs mapping`);
            remainingMappings.push({
              externalNickname: player.nickname,
              localPlayerId: null,
              position: player.position,
              requiresMapping: true
            });
          }
        }

        // If there are more mappings needed, show the next one
        if (remainingMappings.length > 0) {
          setCurrentMapping(remainingMappings[0]);
          setMappingDialogOpen(true);
        } else {
          // All players are mapped, proceed with game creation
          await createGameAndResults();
        }
      }
    } catch (error: any) {
      console.error('Error saving player mapping:', error);
      showSnackbar('Failed to save player mapping: ' + error.message, 'error');
    }
  };

  const createGameAndResults = async (isBatchProcessing: boolean = false) => {
    if (!scrapedGame || !leagueId) return;

    try {
      // Find judge ID
      const judgeId = findJudgeByNickname(scrapedGame.judge);
      if (!judgeId) {
        throw new Error(`Judge "${scrapedGame.judge}" not found`);
      }

      // Get current user role to check permissions
      const { getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      const superAdmin = await isSuperAdmin();
      
      // Verify the admin has access to create games in this league
      if (!superAdmin && userRole?.club_id) {
        // Fetch the league to check if it belongs to the admin's club
        const { data: leagueData } = await supabase
          .from('leagues')
          .select('club_id')
          .eq('id', leagueId)
          .single();
          
        if (!leagueData || leagueData.club_id !== userRole.club_id) {
          throw new Error('You do not have permission to create games in this league');
        }
      }

      // Create the game
      const gameData = {
        session_num: sessionNum,
        number_in_session: gameNumber,
        league_id: leagueId,
        occurred_at: new Date(scrapedGame.date).toISOString(),
        winner: convertScrapedWinnerToEnum(scrapedGame.winner),
        judge_id: judgeId,
        video_link: scrapedGame.videoLink || null
      };

      console.log('Creating game with data:', gameData);
      const { data: gameInsertData, error: gameError } = await supabase
        .from('games')
        .insert([gameData])
        .select();

      if (gameError) throw gameError;
      if (!gameInsertData || gameInsertData.length === 0) {
        throw new Error('Failed to create game');
      }

      const gameId = gameInsertData[0].id;
      console.log('Game created with ID:', gameId);

      // Create game results for each player
      for (const player of scrapedGame.players) {
        // Use the enhanced findPlayerByNickname function which checks all matching possibilities
        const playerId = findPlayerByNickname(player.nickname);
        
        if (!playerId) {
          throw new Error(`Player "${player.nickname}" not found and not mapped`);
        }

        const resultData = {
          game_id: gameId,
          player_id: playerId,
          is_win: player.isWin,
          bonus_points: player.bonusPoints,
          role: convertScrapedRoleToEnum(player.role),
          is_killed_first: player.isKilledFirst,
          winning_move: player.winningMove || null,
          comment: null, // Could add comments from scraper if available
          player_num: player.position
        };

        console.log('Creating result for player:', player.nickname, resultData);
        const { error: resultError } = await supabase
          .from('game_results')
          .insert([resultData]);

        if (resultError) {
          // If there's an error with one player, log it but continue with others
          console.error(`Error creating result for player ${player.nickname}:`, resultError);
        }
      }

      // Show success message
      if (isBatchProcessing) {
        // In batch mode, show a simple message for each game
        console.log(`Game ${scrapedGame.gameId} imported successfully with DB ID: ${gameId}`);
        showSnackbar(`Game ${scrapedGame.game_num || scrapedGame.gameId} imported`, 'success');
      } else {
        // In regular mode, show standard success message and navigate
        showSnackbar('Game and results created successfully!', 'success');
        
        // Navigate to the Game Results tab for the new game
        navigate(`/admin?tab=4&game=${gameId}`);
      }
      
      // During batch processing, remove the game from the selected games
      if (isBatchProcessing) {
        const newSelectedGames = new Set(selectedGames);
        newSelectedGames.delete(scrapedGame.gameId);
        setSelectedGames(newSelectedGames);
      }
    } catch (error: any) {
      console.error('Error creating game and results:', error);
      showSnackbar('Error: ' + error.message, 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Game Data Scraper
      </Typography>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography variant="body1">
            Enter a Mafia game URL or game list URL to import game data.
            {isGameList ? ' (Game list URLs look like: https://the-mafia.net/rate-game-list/141848)' 
                       : ' (Game URLs look like: https://the-mafia.net/rate_game/141867)'}
          </Typography>

          <TextField
            label={isGameList ? "Game List URL" : "Game URL"}
            variant="outlined"
            fullWidth
            value={url}
            onChange={handleUrlChange}
            placeholder={isGameList 
              ? "https://the-mafia.net/rate-game-list/141848" 
              : "https://the-mafia.net/rate_game/141867"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon />
                </InputAdornment>
              ),
            }}
          />

          {isGameList && gameListItems.length > 0 && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Found {gameListItems.length} games in this list
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Selected: {selectedGames.size} / {gameListItems.length} games
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={handleSelectAllGames}
                      disabled={gameListItems.length === 0 || processingBatchImport || isBatchProcessing}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={handleDeselectAllGames}
                      disabled={selectedGames.size === 0 || processingBatchImport || isBatchProcessing}
                    >
                      Deselect All
                    </Button>
                  </Box>
                </Box>
                
                <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" width="5%"></TableCell>
                        <TableCell width="15%">Game #</TableCell>
                        <TableCell width="55%">Title</TableCell>
                        <TableCell width="25%">Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gameListItems.map(game => (
                        <TableRow 
                          key={game.gameId}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleToggleSelectGame(game.gameId)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedGames.has(game.gameId)}
                              onChange={() => handleToggleSelectGame(game.gameId)}
                              onClick={(e) => e.stopPropagation()} // Prevent double toggle
                              disabled={processingBatchImport || isBatchProcessing}
                            />
                          </TableCell>
                          <TableCell>{game.number || '-'}</TableCell>
                          <TableCell>{game.title}</TableCell>
                          <TableCell>{game.date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Button 
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleImportSelectedGames}
                  disabled={selectedGames.size === 0 || processingBatchImport || isBatchProcessing}
                  sx={{ mt: 2 }}
                >
                  {(processingBatchImport || isBatchProcessing) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>
                        {isBatchProcessing ? 
                          `Importing ${currentBatchIndex + 1}/${totalGamesInBatch}...` :
                          'Preparing import...'
                        }
                      </span>
                    </Box>
                  ) : (
                    `Import ${selectedGames.size} Selected Games`
                  )}
                </Button>
              </Box>
            </>
          )}

          {/* Only show this button when we either have no game list data yet, or we're in single game mode */}
          {(!isGameList || (isGameList && gameListItems.length === 0)) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleScrape}
              disabled={loading || !url}
              sx={{ borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : (
                isGameList 
                  ? 'Get Game List' 
                  : 'Scrape Game Data'
              )}
            </Button>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {scrapedGame && (
        <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import Game
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>Game Details</Typography>
              <Typography><strong>Date:</strong> {scrapedGame.date}</Typography>
              <Typography><strong>Judge:</strong> {scrapedGame.judge}</Typography>
              <Typography><strong>Winner:</strong> {scrapedGame.winner}</Typography>
              <Typography><strong>Players:</strong> {scrapedGame.players.length}</Typography>
              {scrapedGame.game_num && (
                <Typography><strong>Game Number:</strong> {scrapedGame.game_num} (Maps to Session {Math.ceil(scrapedGame.game_num / 5)}, Game {((scrapedGame.game_num - 1) % 5) + 1})</Typography>
              )}
              {scrapedGame.videoLink && (
                <Typography>
                  <strong>Video Link:</strong>{' '}
                  <a href={scrapedGame.videoLink} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    {scrapedGame.videoLink}
                  </a>
                </Typography>
              )}
            </Box>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>Import Settings</Typography>
              
              <Stack spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>League</InputLabel>
                  <Select
                    value={leagueId}
                    onChange={(e) => setLeagueId(e.target.value)}
                    label="League"
                  >
                    {leagues.length === 0 ? (
                      <MenuItem disabled value="">
                        No leagues available for this judge
                      </MenuItem>
                    ) : (
                      leagues.map(league => (
                        <MenuItem key={league.id} value={league.id}>
                          {league.name} ({league.club?.name || 'Unknown Club'}) 
                          - Judge: {league.judge?.nickname || 'Unknown'}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Session Number"
                    type="number"
                    value={sessionNum}
                    onChange={(e) => setSessionNum(Number(e.target.value))}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                  
                  <TextField
                    label="Game Number"
                    type="number"
                    value={gameNumber}
                    onChange={(e) => setGameNumber(Number(e.target.value))}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Stack>
              </Stack>
            </Box>
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => createGame()}
              disabled={!scrapedGame || !leagueId || processingBatchImport}
              sx={{ mt: 2 }}
            >
              Create Game & Results
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Player Mapping Dialog */}
      {currentMapping && (
        <PlayerMappingDialog
          open={mappingDialogOpen}
          onClose={() => setMappingDialogOpen(false)}
          playerMapping={currentMapping}
          players={players}
          onSave={handleSavePlayerMapping}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GameScraper;